import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bell, X } from 'lucide-react'
import Modal from './ui/Modal'
import { useAuth } from '../contexts/AuthContext'
import { usePushSubscription } from '../hooks/usePushSubscription'

// First-launch prompt asking the user to enable notifications.
// Driven by useNotificationPrompt → only mounts when the browser
// permission is still 'default' and we haven't asked this session.
//
// Grant flow auto-subscribes the device to the daily 9 AM check-in
// reminder via usePushSubscription.enable. The user can change the
// time later from Settings; without this the prompt-on-open would
// just earn us permission with no actual reminder behind it.
export default function NotificationPromptModal({ onClose, onPermissionChange }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { enable, busy } = usePushSubscription()
  const [error, setError] = useState(null)

  const handleEnable = async () => {
    setError(null)
    if (!user) {
      // Signed-out modal shouldn't happen (mount is gated on user) but
      // be defensive — without an auth row we can't write to
      // push_subscriptions, so just close.
      onClose()
      return
    }
    // 9 AM local → matches the auto-subscribe contract the owner
    // confirmed. enable() handles requestPermission + SW subscribe +
    // push_subscriptions + notification_preferences in one go.
    const ok = await enable(9, 0)
    if (ok) {
      onPermissionChange?.('granted')
      onClose()
      return
    }
    // enable() returns false on either denied permission or a network
    // failure during subscribe. Mirror the real browser state out so
    // the banner takes over correctly.
    const next = typeof Notification !== 'undefined' ? Notification.permission : 'denied'
    onPermissionChange?.(next)
    if (next === 'denied') {
      // Browser blocked us — no point retrying in the modal, just
      // close so the banner can guide them to settings.
      onClose()
    } else {
      setError(t('notifications.enableFailed'))
    }
  }

  return (
    <Modal open onClose={onClose} ariaLabel={t('notifications.promptTitle')}>
      <div className="flex items-center gap-2 mb-3">
        <span className="p-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
          <Bell className="w-5 h-5" />
        </span>
        <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">
          {t('notifications.promptTitle')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('settings.close')}
          className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <p className="text-sm text-foreground mb-4" dir="auto">
        {t('notifications.promptBody')}
      </p>
      {error && (
        <p className="text-xs text-red-500 text-center mb-2" dir="auto">{error}</p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="flex-1 py-2 rounded-md border-2 border-border text-sm text-foreground hover:bg-accent disabled:opacity-50"
          dir="auto"
        >
          {t('notifications.notNow')}
        </button>
        <button
          type="button"
          onClick={handleEnable}
          disabled={busy}
          className="flex-1 py-2 rounded-md bg-app-primary-500 text-white text-sm font-semibold disabled:opacity-50"
          dir="auto"
        >
          {busy ? t('notifications.enabling') : t('notifications.enable')}
        </button>
      </div>
    </Modal>
  )
}
